import { BaseScene } from './BaseScene';
import { UserProfile, buyEarth, buyTower, buyPet, equipEarth, equipTower, equipPet, unequipTower, unequipPet, saveUserProfile, buyEquipment, upgradeEquipment, equipEquipment, unequipEquipment, combineEquipment, getEquipmentManager } from '../entities/base/UserProfile';
import { ALL_EQUIPMENT_CONFIGS, EquipmentType, RARITY_COLORS, getEquipmentsByType } from '../data/EquipmentData';
import { Equipment } from '../entities/equipment/Equipment';
import { t } from '../i18n/lang';

interface ShopItem {
  key: string;
  name: string;
  description: string;
  cost: number;
  currency: 'gold' | 'ruby';
  unlockLevel?: number;
}

const EARTH_SHOP: ShopItem[] = [
  { key: 'classic', name: 'Classic Earth', description: 'Balanced stats, good for beginners', cost: 0, currency: 'gold' },
  { key: 'ice', name: 'Ice Earth', description: 'High health, can freeze monsters', cost: 500, currency: 'gold', unlockLevel: 5 },
  { key: 'volcanic', name: 'Volcanic Earth', description: 'Reflects damage, many towers', cost: 800, currency: 'gold', unlockLevel: 10 },
  { key: 'crystal', name: 'Crystal Earth', description: 'High resistance, generates energy', cost: 1200, currency: 'gold', unlockLevel: 15 },
  { key: 'quantum', name: 'Quantum Earth', description: 'Can teleport towers', cost: 1500, currency: 'ruby', unlockLevel: 20 },
  { key: 'neutron', name: 'Neutron Earth', description: 'Very high health, slows monsters', cost: 2000, currency: 'ruby', unlockLevel: 25 },
];

const TOWER_SHOP: ShopItem[] = [
  { key: 'laser', name: 'Laser Tower', description: 'Balanced damage and speed', cost: 0, currency: 'gold' },
  { key: 'rapid', name: 'Rapid Fire Tower', description: 'High speed, low damage', cost: 200, currency: 'gold' },
  { key: 'heavy', name: 'Heavy Cannon', description: 'High damage, slow speed', cost: 300, currency: 'gold' },
  { key: 'missile', name: 'Missile Tower', description: 'Area damage, slow reload', cost: 400, currency: 'gold' },
  { key: 'freeze', name: 'Freeze Tower', description: 'Slows or freezes monsters', cost: 350, currency: 'gold' },
  { key: 'multi', name: 'Multi-Directional', description: 'Shoots in 4 directions', cost: 500, currency: 'gold', unlockLevel: 4 },
  { key: 'piercing', name: 'Piercing Tower', description: 'Pierces through 3 monsters', cost: 600, currency: 'gold', unlockLevel: 6 },
  { key: 'chain', name: 'Chain Lightning', description: 'Lightning chains to 3 targets', cost: 700, currency: 'gold', unlockLevel: 8 },
  { key: 'shotgun', name: 'Shotgun Tower', description: 'Fires spread of 5 projectiles', cost: 450, currency: 'gold', unlockLevel: 10 },
  { key: 'beam', name: 'Beam Tower', description: 'Continuous laser beam', cost: 800, currency: 'ruby', unlockLevel: 12 },
];

const PET_SHOP: ShopItem[] = [
  { key: 'laser', name: 'Laser Pet', description: 'Shoots laser beams at monsters', cost: 0, currency: 'gold' },
  { key: 'missile', name: 'Missile Pet', description: 'Fires homing missiles', cost: 300, currency: 'gold', unlockLevel: 10 },
  { key: 'drone', name: 'Drone Pet', description: 'Rapid-fire attacks with mobility', cost: 500, currency: 'gold', unlockLevel: 20 },
  { key: 'shield', name: 'Shield Pet', description: 'Protects Earth and reflects damage', cost: 700, currency: 'ruby', unlockLevel: 30 },
  { key: 'support', name: 'Support Pet', description: 'Buffs towers and Earth', cost: 1000, currency: 'ruby', unlockLevel: 40 },
];

export class ProfileScene extends BaseScene {
  private userProfile: UserProfile;
  private currentTab: 'earth' | 'tower' | 'pet' | 'item' = 'earth';
  private goldText: Phaser.GameObjects.Text;
  private rubyText: Phaser.GameObjects.Text;
  private contentContainer: Phaser.GameObjects.Container;
  private selectedEquipments: string[] = []; // Cho combine feature

  constructor() {
    super({ key: 'Profile' });
  }

  override create(): void {
    super.create();
    this.addBackground();
    
    // Load user profile from global state
    this.userProfile = (window as any).userProfile;
    
    this.createModernUI();
  }

  private createModernUI(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    
    // Create main container
    this.contentContainer = this.add.container(0, 0);
    this.contentContainer.setDepth(1000);
    
    // Create header section
    this.createHeader();
    
    // Create tab navigation
    this.createTabNavigation();
    
    // Create currency display
    this.createCurrencyDisplay();
    
    // Create content area
    this.createContentArea();
    
    // Show default tab content
    this.showTabContent('earth');
  }

  private createHeader(): void {
    const width = this.cameras.main.width;
    
    // Header background
    const headerBg = this.add.graphics();
    headerBg.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 0.95);
    headerBg.fillRoundedRect(0, 0, width, 100, 0);
    headerBg.lineStyle(2, 0x74b9ff, 0.8);
    headerBg.strokeRect(0, 98, width, 2);
    headerBg.setDepth(1001);
    
    // Title
    this.add.text(width / 2, 35, 'PLAYER PROFILE', {
      fontSize: '32px',
      fontFamily: 'Arial Black',
      color: '#74b9ff',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(1002);
    
    // Subtitle
    this.add.text(width / 2, 65, 'Manage your arsenal and equipment', {
      fontSize: '14px',
      fontFamily: 'Arial',
      color: '#bdc3c7',
      fontStyle: 'italic'
    }).setOrigin(0.5).setDepth(1002);
    
    // Back button with modern styling
    this.createBackButton();
  }

  private createBackButton(): void {
    const buttonContainer = this.add.container(60, 50);
    buttonContainer.setDepth(1003);
    
    // Button background
    const buttonBg = this.add.graphics();
    buttonBg.fillGradientStyle(0x95a5a6, 0x7f8c8d, 0x95a5a6, 0x7f8c8d, 1);
    buttonBg.fillRoundedRect(-35, -20, 70, 40, 20);
    buttonBg.lineStyle(2, 0xbdc3c7, 0.8);
    buttonBg.strokeRoundedRect(-35, -20, 70, 40, 20);
    buttonContainer.add(buttonBg);
    
    // Back icon and text
    const backIcon = this.add.text(-15, 0, '←', {
      fontSize: '20px',
      fontFamily: 'Arial Black',
      color: '#ffffff'
    }).setOrigin(0.5);
    buttonContainer.add(backIcon);
    
    const backText = this.add.text(8, 0, 'BACK', {
      fontSize: '12px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    buttonContainer.add(backText);
    
    // Interactive
    buttonContainer.setInteractive(
      new Phaser.Geom.Rectangle(-35, -20, 70, 40),
      Phaser.Geom.Rectangle.Contains
    );
    
    buttonContainer.on('pointerover', () => {
      this.tweens.add({
        targets: buttonContainer,
        scaleX: 1.05,
        scaleY: 1.05,
        duration: 200,
        ease: 'Power2'
      });
    });
    
    buttonContainer.on('pointerout', () => {
      this.tweens.add({
        targets: buttonContainer,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: 'Power2'
      });
    });
    
    buttonContainer.on('pointerdown', () => {
      this.scene.start('MainMenu');
    });
  }

  private createTabNavigation(): void {
    const width = this.cameras.main.width;
    const tabY = 120;
    
    const tabs = [
      { key: 'earth', name: 'EARTH', icon: '🌍', color: 0x27ae60 },
      { key: 'tower', name: 'TOWERS', icon: '🏗️', color: 0x3498db },
      { key: 'pet', name: 'PETS', icon: '🐾', color: 0x9b59b6 },
      { key: 'item', name: 'EQUIPMENT', icon: '⚔️', color: 0xf39c12 }
    ];
    
    const tabWidth = (width - 40) / tabs.length;
    
    tabs.forEach((tab, index) => {
      const tabX = 20 + (index * tabWidth) + (tabWidth / 2);
      this.createTab(tab, tabX, tabY, tabWidth);
    });
  }

  private createTab(tab: any, x: number, y: number, width: number): void {
    const tabContainer = this.add.container(x, y);
    tabContainer.setDepth(1004);
    
    // Tab background
    const isActive = this.currentTab === tab.key;
    const tabBg = this.add.graphics();
    
    if (isActive) {
      tabBg.fillGradientStyle(tab.color, tab.color * 0.8, tab.color, tab.color * 0.8, 1);
      tabBg.lineStyle(2, 0xffffff, 1);
    } else {
      tabBg.fillStyle(0x34495e, 0.8);
      tabBg.lineStyle(2, 0x7f8c8d, 0.6);
    }
    
    tabBg.fillRoundedRect(-width/2 + 5, -25, width - 10, 50, 25);
    tabBg.strokeRoundedRect(-width/2 + 5, -25, width - 10, 50, 25);
    tabContainer.add(tabBg);
    
    // Tab icon
    const icon = this.add.text(0, -8, tab.icon, {
      fontSize: '20px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    tabContainer.add(icon);
    
    // Tab text
    const text = this.add.text(0, 8, tab.name, {
      fontSize: '12px',
      fontFamily: 'Arial Black',
      color: isActive ? '#ffffff' : '#bdc3c7',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    tabContainer.add(text);
    
    // Interactive
    tabContainer.setInteractive(
      new Phaser.Geom.Rectangle(-width/2 + 5, -25, width - 10, 50),
      Phaser.Geom.Rectangle.Contains
    );
    
    tabContainer.on('pointerover', () => {
      if (this.currentTab !== tab.key) {
        this.tweens.add({
          targets: tabContainer,
          scaleX: 1.05,
          scaleY: 1.05,
          duration: 200,
          ease: 'Power2'
        });
      }
    });
    
    tabContainer.on('pointerout', () => {
      if (this.currentTab !== tab.key) {
        this.tweens.add({
          targets: tabContainer,
          scaleX: 1,
          scaleY: 1,
          duration: 200,
          ease: 'Power2'
        });
      }
    });
    
    tabContainer.on('pointerdown', () => {
      this.currentTab = tab.key as any;
      this.refreshUI();
    });
    
    // Store reference for updates
    tabContainer.setData('tabKey', tab.key);
    tabContainer.setData('tabBg', tabBg);
    tabContainer.setData('tabText', text);
    tabContainer.setData('tabColor', tab.color);
  }

  private createCurrencyDisplay(): void {
    const width = this.cameras.main.width;
    
    // Currency panel background
    const currencyBg = this.add.graphics();
    currencyBg.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 0.95);
    currencyBg.fillRoundedRect(width - 250, 185, 230, 60, 10);
    currencyBg.lineStyle(2, 0xf39c12, 0.8);
    currencyBg.strokeRoundedRect(width - 250, 185, 230, 60, 10);
    currencyBg.setDepth(1005);
    
    // Gold display
    const goldIcon = this.add.graphics();
    goldIcon.fillStyle(0xf1c40f, 1);
    goldIcon.fillCircle(width - 230, 205, 12);
    goldIcon.lineStyle(2, 0xf39c12, 1);
    goldIcon.strokeCircle(width - 230, 205, 12);
    goldIcon.setDepth(1006);
    
    this.add.text(width - 210, 195, 'GOLD', {
      fontSize: '10px',
      fontFamily: 'Arial Black',
      color: '#f1c40f',
      fontStyle: 'bold'
    }).setDepth(1006);
    
    this.goldText = this.add.text(width - 210, 210, `${this.userProfile.gold}`, {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setDepth(1006);
    
    // Ruby display
    const rubyIcon = this.add.graphics();
    rubyIcon.fillStyle(0xe74c3c, 1);
    rubyIcon.fillCircle(width - 120, 205, 12);
    rubyIcon.lineStyle(2, 0xc0392b, 1);
    rubyIcon.strokeCircle(width - 120, 205, 12);
    rubyIcon.setDepth(1006);
    
    this.add.text(width - 120, 205, '💎', {
      fontSize: '16px',
      fontFamily: 'Arial'
    }).setOrigin(0.5).setDepth(1007);
    
    this.add.text(width - 100, 195, 'RUBY', {
      fontSize: '10px',
      fontFamily: 'Arial Black',
      color: '#e74c3c',
      fontStyle: 'bold'
    }).setDepth(1006);
    
    this.rubyText = this.add.text(width - 100, 210, `${this.userProfile.ruby}`, {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setDepth(1006);
  }

  private createContentArea(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    
    // Content area background
    const contentBg = this.add.graphics();
    contentBg.fillGradientStyle(0x34495e, 0x2c3e50, 0x34495e, 0x2c3e50, 0.9);
    contentBg.fillRoundedRect(20, 260, width - 40, height - 300, 15);
    contentBg.lineStyle(2, 0x74b9ff, 0.5);
    contentBg.strokeRoundedRect(20, 260, width - 40, height - 300, 15);
    contentBg.setDepth(1008);
  }

  private showTabContent(tabKey: string): void {
    // Clear existing content
    this.clearTabContent();
    
    // Show content based on tab
    switch (tabKey) {
      case 'earth':
        this.createEarthContent();
        break;
      case 'tower':
        this.createTowerContent();
        break;
      case 'pet':
        this.createPetContent();
        break;
      case 'item':
        this.createItemContent();
        break;
    }
  }

  private clearTabContent(): void {
    // Remove all content items (keep UI elements)
    this.contentContainer.list = this.contentContainer.list.filter((child: any) => {
      if (child.y && child.y > 250) { // Content starts after tabs
        child.destroy();
        return false;
      }
      return true;
    });
  }

  private refreshUI(): void {
    // Update tab appearance
    this.children.list.forEach((child: any) => {
      if (child.list && child.getData && child.getData('tabKey')) {
        const tabKey = child.getData('tabKey');
        const tabBg = child.getData('tabBg');
        const tabText = child.getData('tabText');
        const tabColor = child.getData('tabColor');
        
        const isActive = this.currentTab === tabKey;
        
        // Update tab background
        tabBg.clear();
        if (isActive) {
          tabBg.fillGradientStyle(tabColor, tabColor * 0.8, tabColor, tabColor * 0.8, 1);
          tabBg.lineStyle(2, 0xffffff, 1);
        } else {
          tabBg.fillStyle(0x34495e, 0.8);
          tabBg.lineStyle(2, 0x7f8c8d, 0.6);
        }
        
        const width = this.cameras.main.width;
        const tabWidth = (width - 40) / 4;
        tabBg.fillRoundedRect(-tabWidth/2 + 5, -25, tabWidth - 10, 50, 25);
        tabBg.strokeRoundedRect(-tabWidth/2 + 5, -25, tabWidth - 10, 50, 25);
        
        // Update tab text color
        tabText.setColor(isActive ? '#ffffff' : '#bdc3c7');
      }
    });
    
    // Update currency display
    this.updateCurrencyDisplay();
    
    // Show new tab content
    this.showTabContent(this.currentTab);
  }

  private updateContent(): void {
    this.showTabContent(this.currentTab);
  }

  private createEarthContent(): void {
    const startY = 220;
    const itemHeight = 80;
    
    EARTH_SHOP.forEach((item, index) => {
      const y = startY + index * itemHeight;
      const isOwned = this.userProfile.ownedEarths.includes(item.key);
      const isEquipped = this.userProfile.equippedEarth === item.key;
      const canAfford = this.userProfile[item.currency] >= item.cost;
      const canUnlock = !item.unlockLevel || this.userProfile.highestLevel >= item.unlockLevel;
      
      this.createShopItem(item, y, isOwned, isEquipped, canAfford, canUnlock, 'earth');
    });
  }

  private createTowerContent(): void {
    const startY = 220;
    const itemHeight = 80;
    
    TOWER_SHOP.forEach((item, index) => {
      const y = startY + index * itemHeight;
      const isOwned = this.userProfile.ownedTowers.includes(item.key);
      const isEquipped = this.userProfile.equippedTowers.includes(item.key);
      const canAfford = this.userProfile[item.currency] >= item.cost;
      const canUnlock = !item.unlockLevel || this.userProfile.highestLevel >= item.unlockLevel;
      
      this.createShopItem(item, y, isOwned, isEquipped, canAfford, canUnlock, 'tower');
    });
  }

  private createPetContent(): void {
    const startY = 220;
    const itemHeight = 80;
    
    PET_SHOP.forEach((item, index) => {
      const y = startY + index * itemHeight;
      const isOwned = this.userProfile.ownedPets.includes(item.key);
      const isEquipped = this.userProfile.equippedPets.includes(item.key);
      const canAfford = this.userProfile[item.currency] >= item.cost;
      const canUnlock = !item.unlockLevel || this.userProfile.highestLevel >= item.unlockLevel;
      
      this.createShopItem(item, y, isOwned, isEquipped, canAfford, canUnlock, 'pet');
    });
  }

  private createItemContent(): void {
    const equipmentManager = getEquipmentManager();
    
    // Equipment type tabs
    this.createEquipmentTypeTabs();
    
    // Hiển thị equipment theo type
    this.displayEquipmentByType(EquipmentType.WEAPON);
  }

  private createEquipmentTypeTabs(): void {
    const types = [
      { type: EquipmentType.WEAPON, name: 'WEAPONS' },
      { type: EquipmentType.ARMOR, name: 'ARMOR' },
      { type: EquipmentType.ACCESSORY, name: 'ACCESSORIES' },
      { type: EquipmentType.SPECIAL, name: 'SPECIAL' },
    ];

    const tabWidth = 120;
    const startX = this.cameras.main.centerX - (types.length * tabWidth) / 2;

    types.forEach((typeInfo, index) => {
      const x = startX + index * tabWidth;
      const isActive = index === 0; // Default to first tab
      
      const tabBtn = this.add.text(x, 200, typeInfo.name, {
        fontSize: '14px',
        color: isActive ? '#FFD700' : '#ffffff',
        backgroundColor: isActive ? '#444444' : '#222222',
        padding: { x: 15, y: 8 },
        fontFamily: 'Arial',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      
      tabBtn.on('pointerdown', () => {
        this.displayEquipmentByType(typeInfo.type);
        // Update tab colors
        this.contentContainer.list.forEach((child: any) => {
          if (child.text && types.some(t => t.name === child.text)) {
            child.setStyle({ 
              color: child.text === typeInfo.name ? '#FFD700' : '#ffffff',
              backgroundColor: child.text === typeInfo.name ? '#444444' : '#222222'
            });
          }
        });
      });
      
      this.contentContainer.add(tabBtn);
    });
  }

  private displayEquipmentByType(type: EquipmentType): void {
    // Clear existing equipment display
    this.contentContainer.list = this.contentContainer.list.filter((child: any) => {
      if (child.y > 240) { // Equipment items start below tabs
        child.destroy();
        return false;
      }
      return true;
    });

    const equipmentManager = getEquipmentManager();
    const ownedEquipments = equipmentManager.getEquipmentsByType(type);
    const availableConfigs = getEquipmentsByType(type);

    let yOffset = 250;

    // Shop section
    const shopText = this.add.text(this.cameras.main.centerX, yOffset, 'SHOP', {
      fontSize: '18px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    this.contentContainer.add(shopText);
    yOffset += 30;

    // Available equipment to buy
    availableConfigs.forEach(config => {
      const canAfford = this.userProfile[config.currency] >= config.cost;
      const canUnlock = !config.unlockLevel || this.userProfile.highestLevel >= config.unlockLevel;
      
      this.createEquipmentShopItem(config, yOffset, canAfford, canUnlock);
      yOffset += 60;
    });

    yOffset += 20;

    // Inventory section
    const inventoryText = this.add.text(this.cameras.main.centerX, yOffset, 'INVENTORY', {
        fontSize: '18px',
        color: '#4CAF50',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    this.contentContainer.add(inventoryText);
    yOffset += 30;

    // Owned equipment
    if (ownedEquipments.length === 0) {
      const noItemsText = this.add.text(this.cameras.main.centerX, yOffset, 'No equipment owned', {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      this.contentContainer.add(noItemsText);
    } else {
      ownedEquipments.forEach(equipment => {
        this.createEquipmentInventoryItem(equipment, yOffset);
        yOffset += 80;
      });
    }

    // Combine section
    if (ownedEquipments.length >= 3) {
      yOffset += 20;
      this.createCombineSection(type, yOffset);
    }
  }

  private createEquipmentShopItem(config: any, y: number, canAfford: boolean, canUnlock: boolean): void {
    const centerX = this.cameras.main.centerX;
    
    // Background
    const bg = this.add.rectangle(centerX, y, 700, 50, 0x333333, 0.8);
    this.contentContainer.add(bg);
    
    // Name with rarity color
    const nameText = this.add.text(centerX - 300, y, config.name, {
      fontSize: '16px',
      color: RARITY_COLORS[config.rarity as keyof typeof RARITY_COLORS],
      fontFamily: 'Arial',
    }).setOrigin(0, 0.5);
    this.contentContainer.add(nameText);
    
    // Cost
    const costText = this.add.text(centerX, y, `${config.cost} ${config.currency.toUpperCase()}`, {
      fontSize: '14px',
      color: canAfford ? '#FFD700' : '#ff6666',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    this.contentContainer.add(costText);
    
    // Buy button or lock status
    if (!canUnlock) {
      const lockText = this.add.text(centerX + 200, y, `Unlock Lv.${config.unlockLevel}`, {
        fontSize: '12px',
        color: '#ff6666',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      this.contentContainer.add(lockText);
    } else {
      const buyBtn = this.add.text(centerX + 200, y, 'BUY', {
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: canAfford ? '#4CAF50' : '#666666',
        padding: { x: 12, y: 6 },
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      
      if (canAfford) {
        buyBtn.setInteractive({ useHandCursor: true });
        buyBtn.on('pointerdown', () => {
          if (buyEquipment(this.userProfile, config.id, config.cost, config.currency)) {
            (window as any).userProfile = this.userProfile;
            this.updateCurrencyDisplay();
            this.displayEquipmentByType(config.type);
          }
        });
        buyBtn.on('pointerover', () => buyBtn.setStyle({ backgroundColor: '#388E3C' }));
        buyBtn.on('pointerout', () => buyBtn.setStyle({ backgroundColor: '#4CAF50' }));
      }
      
      this.contentContainer.add(buyBtn);
    }
  }

  private createEquipmentInventoryItem(equipment: Equipment, y: number): void {
    const centerX = this.cameras.main.centerX;
    
    // Background
    const bgColor = equipment.instance.isEquipped ? 0x4CAF50 : 0x444444;
    const bg = this.add.rectangle(centerX, y, 700, 70, bgColor, 0.8);
    this.contentContainer.add(bg);
    
    // Name with level
    const nameText = this.add.text(centerX - 300, y - 15, equipment.getDisplayName(), {
      fontSize: '16px',
      color: RARITY_COLORS[equipment.config.rarity],
      fontFamily: 'Arial',
    }).setOrigin(0, 0.5);
    this.contentContainer.add(nameText);
    
    // Stats preview
    const stats = equipment.getCurrentStats();
    const statsText = this.getStatsPreview(stats);
    const statsDisplay = this.add.text(centerX - 300, y + 10, statsText, {
      fontSize: '12px',
      color: '#cccccc',
      fontFamily: 'Arial',
    }).setOrigin(0, 0.5);
    this.contentContainer.add(statsDisplay);
    
    // Upgrade button
    if (equipment.canUpgrade()) {
      const upgradeCost = equipment.getUpgradeCost();
      const canAfford = this.userProfile[equipment.config.currency] >= upgradeCost;
      
      const upgradeBtn = this.add.text(centerX + 50, y, `UPGRADE (${upgradeCost})`, {
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: canAfford ? '#FF9800' : '#666666',
        padding: { x: 8, y: 4 },
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      
      if (canAfford) {
        upgradeBtn.setInteractive({ useHandCursor: true });
        upgradeBtn.on('pointerdown', () => {
          if (upgradeEquipment(this.userProfile, equipment.instance.instanceId)) {
            (window as any).userProfile = this.userProfile;
            this.updateCurrencyDisplay();
            this.displayEquipmentByType(equipment.config.type);
          }
        });
        upgradeBtn.on('pointerover', () => upgradeBtn.setStyle({ backgroundColor: '#F57C00' }));
        upgradeBtn.on('pointerout', () => upgradeBtn.setStyle({ backgroundColor: '#FF9800' }));
      }
      
      this.contentContainer.add(upgradeBtn);
    }
    
    // Equip/Unequip button
    const equipBtn = this.add.text(centerX + 180, y, equipment.instance.isEquipped ? 'UNEQUIP' : 'EQUIP', {
      fontSize: '12px',
      color: '#ffffff',
      backgroundColor: equipment.instance.isEquipped ? '#ff6666' : '#2196F3',
      padding: { x: 12, y: 6 },
      fontFamily: 'Arial',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    equipBtn.on('pointerdown', () => {
      const success = equipment.instance.isEquipped 
        ? unequipEquipment(this.userProfile, equipment.instance.instanceId)
        : equipEquipment(this.userProfile, equipment.instance.instanceId);
      
      if (success) {
        (window as any).userProfile = this.userProfile;
        this.displayEquipmentByType(equipment.config.type);
      }
    });
    
    this.contentContainer.add(equipBtn);
    
    // Select for combine checkbox
    const isSelected = this.selectedEquipments.includes(equipment.instance.instanceId);
    const selectBtn = this.add.text(centerX + 260, y, isSelected ? '☑' : '☐', {
      fontSize: '16px',
      color: isSelected ? '#4CAF50' : '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    selectBtn.on('pointerdown', () => {
      this.toggleEquipmentSelection(equipment.instance.instanceId);
      this.displayEquipmentByType(equipment.config.type);
    });
    
    this.contentContainer.add(selectBtn);
  }

  private createCombineSection(type: EquipmentType, y: number): void {
    const centerX = this.cameras.main.centerX;
    
    const combineTitle = this.add.text(centerX, y, 'COMBINE (Select 3 same equipment)', {
      fontSize: '16px',
      color: '#9966ff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    this.contentContainer.add(combineTitle);
    
    const selectedCount = this.selectedEquipments.length;
    const canCombine = selectedCount === 3;
    
    const combineBtn = this.add.text(centerX, y + 30, `COMBINE (${selectedCount}/3)`, {
      fontSize: '14px',
      color: '#ffffff',
      backgroundColor: canCombine ? '#9966ff' : '#666666',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    if (canCombine) {
      combineBtn.setInteractive({ useHandCursor: true });
      combineBtn.on('pointerdown', () => {
        if (this.selectedEquipments.length === 3) {
          const success = combineEquipment(
            this.userProfile, 
            this.selectedEquipments[0] as string, 
            this.selectedEquipments[1] as string, 
            this.selectedEquipments[2] as string
          );
          
          if (success) {
            this.selectedEquipments = [];
            (window as any).userProfile = this.userProfile;
            this.displayEquipmentByType(type);
          }
        }
      });
      combineBtn.on('pointerover', () => combineBtn.setStyle({ backgroundColor: '#7b4fb3' }));
      combineBtn.on('pointerout', () => combineBtn.setStyle({ backgroundColor: '#9966ff' }));
    }
    
    this.contentContainer.add(combineBtn);
  }

  private toggleEquipmentSelection(instanceId: string): void {
    const index = this.selectedEquipments.indexOf(instanceId);
    if (index >= 0) {
      this.selectedEquipments.splice(index, 1);
    } else if (this.selectedEquipments.length < 3) {
      this.selectedEquipments.push(instanceId);
    }
  }

  private getStatsPreview(stats: any): string {
    const preview: string[] = [];
    Object.entries(stats).forEach(([key, value]) => {
      if (value !== undefined && value !== 0) {
        const isPercentage = ['attackSpeed', 'critChance', 'critDamage', 'earthDefense', 'goldBonus', 'rubyBonus', 'xpBonus', 'freezeChance', 'burnChance'].includes(key);
        const formattedValue = isPercentage ? `+${((value as number) * 100).toFixed(1)}%` : `+${(value as number).toFixed(0)}`;
        preview.push(`${key}: ${formattedValue}`);
      }
    });
    return preview.slice(0, 3).join(', '); // Chỉ hiện 3 stats đầu
  }

  private createShopItem(item: ShopItem, y: number, isOwned: boolean, isEquipped: boolean, canAfford: boolean, canUnlock: boolean, type: string): void {
    const centerX = this.cameras.main.centerX;
    
    // Background
    const bg = this.add.rectangle(centerX, y, 800, 70, isEquipped ? 0x4CAF50 : (isOwned ? 0x2196F3 : 0x333333), 0.8);
    this.contentContainer.add(bg);
    
    // Name
    const nameText = this.add.text(centerX - 350, y - 15, item.name, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });
    this.contentContainer.add(nameText);
    
    // Description
    const descText = this.add.text(centerX - 350, y + 10, item.description, {
      fontSize: '14px',
      color: '#cccccc',
      fontFamily: 'Arial',
    });
    this.contentContainer.add(descText);
    
    // Cost
    if (item.cost > 0) {
      const costText = this.add.text(centerX + 100, y, `${item.cost} ${item.currency.toUpperCase()}`, {
        fontSize: '16px',
        color: canAfford ? '#FFD700' : '#ff6666',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      this.contentContainer.add(costText);
    }
    
    // Status & Action button
    if (!canUnlock) {
      const lockText = this.add.text(centerX + 250, y, `Unlock Lv.${item.unlockLevel}`, {
        fontSize: '14px',
        color: '#ff6666',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      this.contentContainer.add(lockText);
    } else if (isEquipped) {
      const equippedText = this.add.text(centerX + 250, y, 'EQUIPPED', {
        fontSize: '14px',
        color: '#4CAF50',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      this.contentContainer.add(equippedText);
      
      // Unequip button (chỉ cho tower/pet)
      if (type !== 'earth') {
        const unequipBtn = this.add.text(centerX + 320, y, 'UNEQUIP', {
          fontSize: '12px',
          color: '#ffffff',
          backgroundColor: '#ff6666',
          padding: { x: 8, y: 4 },
          fontFamily: 'Arial',
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });
        
        unequipBtn.on('pointerdown', () => {
          this.unequipItem(item.key, type);
        });
        this.contentContainer.add(unequipBtn);
      }
    } else if (isOwned) {
      const equipBtn = this.add.text(centerX + 250, y, 'EQUIP', {
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#2196F3',
        padding: { x: 12, y: 6 },
        fontFamily: 'Arial',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      
      equipBtn.on('pointerdown', () => {
        this.equipItem(item.key, type);
      });
      equipBtn.on('pointerover', () => equipBtn.setStyle({ backgroundColor: '#1976D2' }));
      equipBtn.on('pointerout', () => equipBtn.setStyle({ backgroundColor: '#2196F3' }));
      this.contentContainer.add(equipBtn);
    } else {
      // Buy button
      const buyBtn = this.add.text(centerX + 250, y, 'BUY', {
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: canAfford ? '#4CAF50' : '#666666',
        padding: { x: 15, y: 6 },
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      
      if (canAfford) {
        buyBtn.setInteractive({ useHandCursor: true });
        buyBtn.on('pointerdown', () => {
          this.buyItem(item, type);
        });
        buyBtn.on('pointerover', () => buyBtn.setStyle({ backgroundColor: '#388E3C' }));
        buyBtn.on('pointerout', () => buyBtn.setStyle({ backgroundColor: '#4CAF50' }));
      }
      
      this.contentContainer.add(buyBtn);
    }
  }

  private buyItem(item: ShopItem, type: string): void {
    let success = false;
    
    switch (type) {
      case 'earth':
        success = buyEarth(this.userProfile, item.key, item.cost);
        break;
      case 'tower':
        success = buyTower(this.userProfile, item.key, item.cost);
        break;
      case 'pet':
        success = buyPet(this.userProfile, item.key, item.cost);
        break;
    }
    
    if (success) {
      (window as any).userProfile = this.userProfile;
      this.updateCurrencyDisplay();
      this.updateContent();
    }
  }

  private equipItem(key: string, type: string): void {
    let success = false;
    
    switch (type) {
      case 'earth':
        success = equipEarth(this.userProfile, key);
        break;
      case 'tower':
        success = equipTower(this.userProfile, key);
        break;
      case 'pet':
        success = equipPet(this.userProfile, key);
        break;
    }
    
    if (success) {
      (window as any).userProfile = this.userProfile;
      this.updateContent();
    }
  }

  private unequipItem(key: string, type: string): void {
    let success = false;
    
    switch (type) {
      case 'tower':
        success = unequipTower(this.userProfile, key);
        break;
      case 'pet':
        success = unequipPet(this.userProfile, key);
        break;
    }
    
    if (success) {
      (window as any).userProfile = this.userProfile;
      this.updateContent();
    }
  }

  private updateCurrencyDisplay(): void {
    this.goldText.setText(`${this.userProfile.gold}`);
    this.rubyText.setText(`${this.userProfile.ruby}`);
  }
} 